import { Link, useParams } from 'react-router-dom'
import { canWriteArea, hasRight, ORDER_STATUSES } from '@wf/shared'
import { ApiError } from '../api/client'
import { useAuth } from '../app/auth'
import { OrderTimer } from '../time/OrderTimer'
import { STATUS_LABEL } from './labels'
import { OrderEdit } from './OrderEdit'
import { OrderItems } from './OrderItems'
import { OrderLinkEdit } from './OrderLinkEdit'
import { OrderMetaEdit } from './OrderMetaEdit'
import { OrderParts } from './OrderParts'
import { OrderTasks } from './OrderTasks'
import { useChangeStatus, useOrder } from './useOrders'

export function OrderDetail() {
  const params = useParams()
  const id = Number(params.id)
  const { user } = useAuth()
  const { data, isLoading, error } = useOrder(id)
  const change = useChangeStatus(id)

  if (isLoading) return <div className="muted">Auftrag wird geladen…</div>
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="error" role="alert">
        Kein Zugriff auf diesen Auftrag.
      </div>
    )
  }
  if (error || !data?.order) {
    return (
      <div className="error" role="alert">
        Auftrag konnte nicht geladen werden.
      </div>
    )
  }
  const order = data.order
  const tasks = data.tasks ?? []
  const parts = data.parts ?? []
  const items = data.items ?? []
  // Aufgaben/Teile verwalten (anlegen/löschen) dürfen Büro-Rollen; abhaken zusätzlich der zugewiesene Mechaniker.
  const canManage = !!user && (user.isSuperadmin || canWriteArea(user, 'annahme') || canWriteArea(user, 'werkstatt') || canWriteArea(user, 'planung'))
  const canToggle = canManage || (!!user && order.mechanicId === user.id)
  // Arbeits-/Leistungs-Positionen (Abrechnung) nur mit Rechnungs-Recht — wie der Alt-Monolith.
  const canRechnung = !!user && canWriteArea(user, 'rechnungen')
  // Auftrag löschen: eigenes Recht (auftrag_loeschen); der Server erzwingt es zusätzlich.
  const canDelete = !!user && hasRight(user, 'auftrag_loeschen')

  // 409 = OCC-Versionskonflikt ODER unerlaubter Statuswechsel — beides reconciled die Mutation bereits.
  const conflict = change.error instanceof ApiError && change.error.status === 409 ? change.error.message : null

  return (
    <section className="wf-view">
      <Link to="/orders" className="back">
        ← Zurück
      </Link>
      <div className="wf-detailpane">
        <div className="wf-detailhead">
          <span className={`wf-pill wf-pill--${order.status}`}>{STATUS_LABEL[order.status] ?? order.status}</span>
          <h2 className="wf-detailhead__title">{order.nr ?? `#${order.id}`}</h2>
        </div>
        {/* Notiz prominent für Nicht-Verwalter (Mechaniker); Verwalter bearbeiten sie unten in OrderMetaEdit. */}
        {order.note && !canManage ? <div className="wf-card__note">{order.note}</div> : null}

        <div className="wf-segment" role="group" aria-label="Status ändern">
          {ORDER_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              className={order.status === status ? 'wf-segment__opt wf-segment__opt--active' : 'wf-segment__opt'}
              aria-pressed={order.status === status}
              disabled={change.isPending}
              onClick={() => {
                if (status !== order.status) change.mutate({ status, version: order.version })
              }}
            >
              {STATUS_LABEL[status] ?? status}
            </button>
          ))}
        </div>

        {conflict ? (
          <p className="warn" role="alert">
            Konflikt: {conflict} – der aktuelle Stand wurde neu geladen.
          </p>
        ) : null}

        <OrderTimer orderId={order.id} />

        <OrderTasks orderId={order.id} tasks={tasks} canToggle={canToggle} canManage={canManage} />

        <OrderParts orderId={order.id} parts={parts} canManage={canManage} />

        {canRechnung ? <OrderItems orderId={order.id} items={items} canManage={canManage} /> : null}

        {canManage ? <OrderLinkEdit order={order} /> : null}

        {canManage ? <OrderMetaEdit order={order} /> : null}

        {canRechnung || canManage || canDelete ? (
          <OrderEdit order={order} canManage={canManage} canRechnung={canRechnung} canDelete={canDelete} />
        ) : null}
      </div>
    </section>
  )
}
